import { useEffect, useMemo, useState } from "react";
import { getPageSections } from "../services/PageBuilder/pageSectionService";
import { useLanguageStore } from "../stores/Language/useLanguageStore";

const CACHE_TTL = 5 * 60 * 1000;
const CACHE_SCHEMA_VERSION = 2;

const getCacheKey = (pageSlug) => `lianne_pb_${pageSlug}`;

const unwrapPageBuilderData = (response) => {
  if (response?.success && response?.data) return response.data;
  if (response?.data?.success && response?.data?.data) return response.data.data;
  return response?.data || response;
};

const getValidCache = (pageSlug) => {
  try {
    const cached = localStorage.getItem(getCacheKey(pageSlug));
    if (!cached) return null;

    const parsed = JSON.parse(cached);
    const isExpired =
      !parsed.timestamp || Date.now() - parsed.timestamp > CACHE_TTL;
    const isLegacyCache = parsed.schemaVersion !== CACHE_SCHEMA_VERSION;

    return isExpired || isLegacyCache ? null : parsed;
  } catch (error) {
    return null;
  }
};

const saveCache = (pageSlug, data) => {
  try {
    localStorage.setItem(
      getCacheKey(pageSlug),
      JSON.stringify({
        schemaVersion: CACHE_SCHEMA_VERSION,
        sections: data.sections || [],
        seo: data.seo || null,
        pageBuilderEnabled: data.page_builder_enabled !== false,
        lastModified: data.last_modified || "",
        timestamp: Date.now(),
      })
    );
  } catch (error) {
    // Bỏ qua lỗi cache để không ảnh hưởng render trang.
  }
};

const isPreviewSectionValid = (section) =>
  Boolean(
    section &&
      section.id &&
      section.type &&
      typeof section.enabled === "boolean" &&
      section.config &&
      typeof section.config === "object"
  );

const getPreviewPayload = (pageSlug) => {
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const previewId = searchParams.get("lpb_preview_id");
    if (!previewId) return null;

    const payload = JSON.parse(
      localStorage.getItem(`lianne_pb_preview_${previewId}`) || "null"
    );

    if (!payload || payload.pageSlug !== pageSlug) return null;
    if (!Array.isArray(payload.sections)) return null;
    if (!payload.sections.every(isPreviewSectionValid)) return null;

    return payload;
  } catch (error) {
    return null;
  }
};

const showPreviewBanner = () => {
  if (document.getElementById("lianne-pb-preview-banner")) return;

  const banner = document.createElement("div");
  banner.id = "lianne-pb-preview-banner";
  banner.textContent = "Đang xem bản nháp Page Builder - chưa publish";
  banner.style.cssText =
    "position:fixed;top:0;left:0;right:0;z-index:2147483647;padding:8px 14px;background:#996800;color:#fff;font:600 13px Inter,Arial,sans-serif;text-align:center;";
  document.body.appendChild(banner);
  document.body.style.paddingTop = "34px";
};

/**
 * Hook fetch sections config cho Page Builder.
 * Hỗ trợ cache stale-while-revalidate và preview postMessage cho Phase 5.
 *
 * @param {string} pageSlug - Slug trang cần render.
 * @returns {{sections: Array|null, seo: Object|null, loading: boolean, error: Error|null, isPreviewMode: boolean}}
 */
export const usePageSections = (pageSlug) => {
  const [sections, setSections] = useState(null);
  const [seo, setSeo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  useEffect(() => {
    const isPreview =
      new URLSearchParams(window.location.search).get("lpb_preview") === "1";
    if (isPreview) {
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    const cached = getValidCache(pageSlug);

    if (cached) {
      setSections(cached.pageBuilderEnabled === false ? [] : cached.sections || []);
      setSeo(cached.seo || null);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const fetchSections = async () => {
      try {
        const response = await getPageSections(pageSlug);
        const data = unwrapPageBuilderData(response);

        if (!isMounted) return;

        // Nếu server trả last_modified khác cache → publish mới → buộc dùng data mới
        const serverModified = data?.last_modified || "";
        const cachedModified = cached?.lastModified || "";
        if (serverModified && cachedModified && serverModified !== cachedModified) {
          // Cache đã stale sau publish, xóa cache cũ
          try {
            localStorage.removeItem(getCacheKey(pageSlug));
          } catch (e) {
            // Bỏ qua lỗi localStorage
          }
        }

        if (data?.page_builder_enabled === false) {
          setSections([]);
          setSeo(data?.seo || null);
          setError(null);
          saveCache(pageSlug, data || {});
          return;
        }

        setSections(data?.sections || []);
        setSeo(data?.seo || null);
        setError(null);
        saveCache(pageSlug, data || {});
      } catch (err) {
        if (!isMounted) return;

        // Nếu đã có cache hợp lệ thì tiếp tục render cache, không ép fallback.
        if (!cached) {
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSections();

    return () => {
      isMounted = false;
    };
  }, [pageSlug]);

  useEffect(() => {
    const isPreview =
      new URLSearchParams(window.location.search).get("lpb_preview") === "1";
    if (!isPreview) return undefined;

    setIsPreviewMode(true);
    showPreviewBanner();

    const previewPayload = getPreviewPayload(pageSlug);
    if (previewPayload) {
      setSections(previewPayload.sections);
      setSeo(previewPayload.seo || null);
      setError(null);
      setLoading(false);
      if (["vi", "en"].includes(previewPayload.language)) {
        const { setLanguage } = useLanguageStore.getState();
        setLanguage(previewPayload.language);
      }
    }

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "LIANNE_PB_PREVIEW") return;

      if (
        event.data.action === "UPDATE_SECTIONS" &&
        Array.isArray(event.data.sections)
      ) {
        if (event.data.pageSlug && event.data.pageSlug !== pageSlug) return;
        if (!event.data.sections.every(isPreviewSectionValid)) return;

        setSections(event.data.sections);
        if (event.data.seo && typeof event.data.seo === "object") {
          setSeo(event.data.seo);
        }
        setLoading(false);
      }

      if (
        event.data.action === "SET_LANGUAGE" &&
        ["vi", "en"].includes(event.data.language)
      ) {
        // Dùng setLanguage() để sync cookie + localStorage + load translations đúng cách
        const { setLanguage } = useLanguageStore.getState();
        setLanguage(event.data.language);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [pageSlug]);

  const activeSections = useMemo(() => {
    if (!sections) return null;

    return [...sections]
      .filter((section) => section?.enabled !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [sections]);

  return { sections: activeSections, seo, loading, error, isPreviewMode };
};
